import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import ChatView from '../component/ChatView'
import ChatSidebar from '../../../Components/Chat/ChatSidebar/container/ChatSidebar'
import ChatMain from '../../../Components/Chat/ChatMain/container/ChatMain'
import { formatRawConversations } from '../../../shared/chat'

import {
  archiveConversation,
  searchConversations,
} from '../../../shared/websockets/chat/chat'
import { authorization } from '../../../Module/Auth/Auth'
import { ROLE_ADMIN } from '../../../shared/constants'
import { getConversationsAsync } from '../../../Module/WS/websockets'
import LoadMore from '../../../Components/LoadMore/LoadMore'
import { debounce } from '../../../Utils/Functions'

const Chat = props => {
  const [title, setTitle] = useState('Chat')
  const [filteredConversations, setConversations] = useState([])
  const [activeConversationId, setActiveConversationId] = useState(null)
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isNewConversation, setIsNewConversation] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [
    shouldShowArchivedConversations,
    setShouldShowArchivedConversations,
  ] = useState(false)
  const [isSpyChat, setIsSpyChat] = useState(false)

  const { role, cityId } = authorization.getUser()
  const { pageStep = 15 } = props

  const { conversationsPageInfo = {} } = props.websockets

  useEffect(() => () => props.clearAllChatData(), [])
  useEffect(() => {
    const initialConversationId =
      props.history.location &&
      props.history.location.state &&
      props.history.location.state.conversationId

    if (
      initialConversationId &&
      props.websockets &&
      props.websockets.conversations &&
      props.websockets.conversations[initialConversationId] &&
      props.websockets.conversations[initialConversationId].name
    ) {
      setActiveConversationData({
        id: initialConversationId,
        name: props.websockets.conversations[initialConversationId].name,
      })
    }
  }, [
    props.history.location,
    props.history.location && props.history.location.state,
    props.history.location &&
      props.history.location.state &&
      props.history.location.state.conversationId,
  ])

  useEffect(() => {
    const { websockets } = props
    if (websockets.connected && !isLoadingConversations) {
      getConversationsAsync({
        ack: 'FETCH_MAIN',
        first: pageStep,
        setLoading: setIsLoadingConversations,
        archived: shouldShowArchivedConversations,
        cityId: role === ROLE_ADMIN && cityId ? null : Number(cityId),
        onlySpyChats: isSpyChat,
      })
    }
  }, [props.websockets.connected, shouldShowArchivedConversations, isSpyChat])

  useEffect(() => {
    const { websockets } = props
    const formattedConversations = formatRawConversations({
      isEndUser: false,
      conversations: Object.values(websockets.conversations),
      messages: websockets.messages,
    })

    const processedConversations = formattedConversations.filter(
      conversation =>
        (shouldShowArchivedConversations
          ? conversation.archived
          : !conversation.archived) &&
        (isSpyChat ? conversation.missionId : !conversation.missionId),
    )

    if (searchValue.length > 0) {
      const filteredConversations = processedConversations.filter(
        conversation =>
          conversation.name.toLowerCase().includes(searchValue.toLowerCase()),
      )
      return setConversations(filteredConversations)
    }

    return setConversations(processedConversations)
  }, [
    props.websockets.conversations,
    props.websockets.messages,
    shouldShowArchivedConversations,
    activeConversationId,
    searchValue,
    isSpyChat,
  ])

  /**
   * Archive conversation handler
   * @param {Number} conversationId - ID of the conversations that should be archived
   */
  const onConversationArchive = conversationId => {
    setActiveConversationId(null)
    setTitle('Chat')
    archiveConversation({ conversationId })
  }

  /**
   * Add conversation handler
   */
  const addConversation = () => {
    setIsNewConversation(true)
    setTitle('New Chat')
    setActiveConversationId(null)
  }

  const onConversationsRefresh = () =>
    getConversationsAsync({
      ack: 'PULL_REFRESH_MAIN',
      first:
        filteredConversations.length > pageStep
          ? filteredConversations.length
          : pageStep,
      setLoading: setIsLoadingConversations,
      archived: shouldShowArchivedConversations,
      cityId: role === ROLE_ADMIN && cityId ? null : Number(cityId),
      onlySpyChats: isSpyChat,
    })

  const onConversationsLoadMore = () =>
    getConversationsAsync({
      ack: 'LOAD_MORE_MAIN',
      after: conversationsPageInfo.endCursor,
      setLoading: setIsLoadingMore,
      first: pageStep,
      archived: shouldShowArchivedConversations,
      cityId: role === ROLE_ADMIN && cityId ? null : Number(cityId),
      onlySpyChats: isSpyChat,
    })

  /**
   * Filters all conversations and sets to the state only the ones which name includes searched string
   *
   * @param {String} searchValue - Value inputted into the conversations search bar
   */
  const onSearchChange = searchValue => {
    setSearchValue(searchValue)
    const searchConversationsDebounce = debounce(
      () =>
        searchConversations({
          searchString: searchValue,
          archived: shouldShowArchivedConversations,
          onlySpyChats: isSpyChat,
        }),
      200,
    )
    searchConversationsDebounce()
  }

  /**
   * When conversation is clicked in conversation list
   * this function sets active conversations id to the
   * state so it can be passed to the main conversation
   * screen and conversations list (to display active item)
   *
   * @param {Number} conversation.id - ID of the clicked conversation item
   * @param {String} conversation.name - Name of the clicked conversation item
   */
  const setActiveConversationData = ({ id, name }) => {
    setActiveConversationId(id)
    setTitle(name)
    setIsNewConversation(false)
  }

  /**
   * Toggles archived/active conversations visibility
   */
  const toggleArchivedConversations = () => {
    setActiveConversationId(null)
    setTitle('Chat')
    setShouldShowArchivedConversations(!shouldShowArchivedConversations)
  }

  return (
    <ChatView
      toggleArchivedConversations={toggleArchivedConversations}
      showingArchivedConversations={shouldShowArchivedConversations}
      isSpyChat={isSpyChat}
      setIsSpyChat={setIsSpyChat}
    >
      <ChatSidebar
        addConversation={addConversation}
        onSearchChange={onSearchChange}
        conversations={filteredConversations}
        onConversationClick={setActiveConversationData}
        onConversationArchive={onConversationArchive}
        activeConversationId={activeConversationId}
        onRefresh={onConversationsRefresh}
        isLoadingConversations={isLoadingConversations}
        loadMoreComponent={
          <LoadMore
            action={onConversationsLoadMore}
            isLoading={isLoadingMore}
            visible={conversationsPageInfo.hasNextPage}
          />
        }
      />
      <ChatMain
        title={title}
        setTitle={setTitle}
        activeConversationId={activeConversationId}
        isNewConversation={isNewConversation}
        setIsNewConversation={setIsNewConversation}
        setActiveConversationId={setActiveConversationId}
        setShouldShowArchivedConversations={setShouldShowArchivedConversations}
        isSpyChat={isSpyChat}
      />
    </ChatView>
  )
}

const mapStateToProps = state => {
  return {
    websockets: state.websockets,
  }
}
const mapDispatchToProps = dispatch => ({
  clearAllChatData: () => {
    dispatch({ type: 'CHAT_CLEAR_ALL_CHAT_DATA' })
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(Chat))



// WEBPACK FOOTER //
// ./src/Containers/Chat/container/Chat.js